import Konva from 'konva';

import HaspObject from './HaspObject';
import { processIconText } from '../utils/iconFonts';
import { FontBinding } from '../services/FontBinding.js';
import { useFontStore } from '../stores/fontStore.js';
import { resolvePadding, onPaddingStyle } from './properties/v2/padding.js';

// val	int16	0	1 = checked
//              0 = unchecked
// text	string	"Checkbox"	The label of the checkbox

export default class HaspCheckBox extends HaspObject {
  // canTransform = false;
  // toggle = false;
  // checked = false;
  // text = "Check box";

  // hassTemplate = '/media/templates/bar.ftl';

  constructor(config) {
    super(config);

    const main = this.style('main');
    const bullet = this.style('bullet');

    this.checkBoxRect = new Konva.Rect({
      x: 0,
      y: 0,
      width: config.width,
      height: config.height,
      fill: this.checked ? bullet.border_color : bullet.bg_color,
      stroke: bullet.border_color,
      strokeWidth: bullet.border_width || 2,
      cornerRadius: bullet.radius,
    });

    this.textObj = new Konva.Text({
      x: (config.width ?? 0) + 5,
      y: 0,
      // height: 15,
      // width: 20,
      verticalAlign: 'middle',
      text: processIconText(this.text),
      fontSize: main.text_font,
      fill: main.text_color,
      listening: false,
      draggable: false,
      strokeWidth: 1,
      align: this.align,
      obj: 'text',
    });
    // LV_LAYOUT_ROW_MID (lv_checkbox.c) centers bullet and label on the same
    // row; the row's own height tracks the label's line height only — the
    // bullet is independently resized to line_height + pad*2 (see
    // LV_SIGNAL_STYLE_CHG in lv_checkbox.c) and is centered on that row, so
    // it overflows above/below the label by exactly `padding` px. Real,
    // visible overflow (e.g. clipped by a page edge at y=0) — not something
    // to hide by growing the object's own height to fit the bullet.
    // Horizontal and vertical padding size the bullet independently, and only
    // the vertical inset offsets the box (see _bulletBox below).
    const box = this._bulletBox(bullet);
    this.checkBoxRect.y(-box.top);
    this.checkBoxRect.width(box.width);
    this.checkBoxRect.height(box.height);
    this.textObj.x(box.width + 5);

    // Create checkmark — follows the bullet's vertical offset, scaled to the
    // smaller box axis so it stays inside a non-square bullet.
    this.checkmark = new Konva.Path({
      x: box.glyph * 0.2,
      y: -box.top + box.height * 0.1,
      data: `M 0 ${box.glyph * 0.5} L ${box.glyph * 0.25} ${box.glyph * 0.7} L ${box.glyph * 0.6} ${box.glyph * 0.1}`,
      stroke: '#ffffff',
      strokeWidth: Math.max(2, box.glyph * 0.1),
      lineCap: 'round',
      lineJoin: 'round',
      listening: false,
      visible: this.checked ?? false,
      obj: 'checkmark',
    });

    this.add(this.checkBoxRect);
    this.add(this.checkmark);
    this.add(this.textObj);
    this._fontBinding = new FontBinding((family) => {
      this.textObj.fontFamily(family);
      this.textObj.getLayer()?.batchDraw();
    });
    this._applyTextFont();
    this.width(this.textObj.width() + this.checkBoxRect.width() + 5);
    this.height(this.textObj.height());
    this.rect.width(this.width());
    this.rect.height(this.height());
    // this.width(this.textObj.width())
    this.draggable(true);

    this.onStyle('main', 'text_color', () => {
      this.textObj.fill(this.style('main').text_color);
    });

    this.on('font', () => {
      this._applyTextFont();
    });

    this.onStyle('main', 'text_font_family', () => {
      // A loaded font resource owns fontFamily while active — FontBinding
      // itself skips the callback in that case (see HaspLabel.js).
      this._applyTextFont();
    });

    this.on('destroy', () => {
      this._fontBinding.dispose();
    });

    this.onStyle('bullet', 'radius', () => {
      this.checkBoxRect.cornerRadius(this.style('bullet').radius);
    });

    // Checked state determines which of bullet's two colors fills the box
    // (border_color when checked, bg_color otherwise) — shared by all three
    // triggers below instead of recomputing the same ternary three times.
    this.on('checked', (e) => {
      this.checkmark.visible(this.checked);
      this._updateBulletFill();
    });

    this.onStyle('bullet', 'border_color', () => {
      this.checkBoxRect.stroke(this.style('bullet').border_color);
      this._updateBulletFill();
    });

    this.onStyle('bullet', 'border_width', () => {
      this.checkBoxRect.strokeWidth(this.style('bullet').border_width);
    });

    this.onStyle('bullet', 'bg_color', () => {
      this._updateBulletFill();
    });

    // Shorthand + all four per-side overrides, one subscription helper.
    onPaddingStyle(this, 'bullet', () => { this.updateCheckbox(); });

    this.onStyle('main', 'text_font', () => {
      this.textObj.fontSize(Number(this.style('main').text_font));
      this.updateCheckbox();
    });

    this.on('text_text', (e) => {
      this.textObj.text(processIconText(this.text));
      this.updateCheckbox();
    });

  }

  setTextLabel(text) {
    this.text = text;
    this.textObj.setAttr('text', processIconText(text));
    this.adjust();
  }

  /**
   * The uploaded Font Resource `this.font` resolves to for canvas preview
   * — see HaspLabel.js's identically-named helper for the full semantics.
   */
  _fontResource() {
    return this.font ? useFontStore().fonts.find(f => f.file === this.font) : null;
  }

  /**
   * Resolve which font family the label text should render with, mirroring
   * HaspLabel.js's _applyTextFont().
   */
  _applyTextFont() {
    const { text_font_family } = this.style('main');
    this._fontBinding.set(this._fontResource(), text_font_family || 'Roboto Condensed');
  }

  // No width/height to resize — the checkbox always auto-fits to its
  // content (see updateCheckbox()). Selecting it still shows the
  // Transformer's border (can_transform in checkbox.js) for visual
  // feedback, just with no resize handles.
  getResizeMode() {
    return 'none';
  }

  _updateBulletFill() {
    const { bg_color, border_color } = this.style('bullet');
    this.checkBoxRect.fill(this.checked ? border_color : bg_color);
  }

  /**
   * The bullet's on-screen box for the current padding: the label's line height
   * grown by the HORIZONTAL padding for width and the VERTICAL padding for
   * height — the only two padding values this widget can express (see
   * checkbox.js's bullet part, which declares PADDING_AXES, not four sides).
   * Also returns the glyph scale the checkmark path uses (the smaller axis, so
   * the mark never spills out of a non-square bullet). Reduces to the old
   * square `line_height + padding * 2` when both axes are equal. Effective
   * values come from v2/padding.js — never re-derived here.
   */
  _bulletBox(bullet = this.style('bullet')) {
    const { left, right, top, bottom } = resolvePadding(bullet);
    const lineHeight = this.textObj.height();
    return {
      width: lineHeight + left + right,
      height: lineHeight + top + bottom,
      top,
      glyph: Math.min(lineHeight + left + right, lineHeight + top + bottom),
    };
  }

  updateCheckbox() {
    const box = this._bulletBox();

    this.checkBoxRect.y(-box.top);
    this.checkBoxRect.width(box.width);
    this.checkBoxRect.height(box.height);
    this.textObj.x(box.width + 5);

    // Update checkmark position and size
    this.checkmark.data(`M 0 ${box.glyph * 0.5} L ${box.glyph * 0.25} ${box.glyph * 0.7} L ${box.glyph * 0.6} ${box.glyph * 0.1}`);
    this.checkmark.x(box.glyph * 0.2);
    this.checkmark.y(-box.top + box.height * 0.1);
    this.checkmark.strokeWidth(Math.max(2, box.glyph * 0.1));

    this.width(this.textObj.width() + this.checkBoxRect.width() + 5);
    this.height(this.textObj.height());
    this.rect.width(this.width());
    this.rect.height(this.height());
    this.idTextObj.moveToTop();
  }

  objectExport(page, objectData) {
    objectData.bg_color = undefined;
    objectData.w = undefined;
    objectData.h = undefined;
    objectData.opacity = undefined;
    objectData.val = this.checked ? 1 : 0;
    // objectData.border_color = undefined;
  }
}
